import { createContext, useContext, useId, type ReactNode } from 'react'
import { cn } from '../../lib/cn'

/*
 * A form's field (PRD 00 §4.6: the server validates, and the form shows the error at the
 * field): its label, the control, an optional hint and the error, tied together for assistive
 * technology. The control (TextInput, DateInput, Select, Combobox) takes its id and its
 * aria-describedby, aria-invalid and required attributes from the field around it.
 */

interface FieldControl {
  id: string
  required: boolean
  invalid: boolean
  /** The hint and the error, as read after the label. */
  describedBy: string | undefined
}

const FieldContext = createContext<FieldControl | null>(null)

/** The attributes a control takes from the Field around it (none outside a Field). */
export function useFieldControl() {
  const field = useContext(FieldContext)
  if (!field) return {}
  return {
    id: field.id,
    required: field.required || undefined,
    'aria-invalid': field.invalid || undefined,
    'aria-describedby': field.describedBy,
  }
}

/** The look of a field's control, whichever it is; red while it has an error. */
export const controlClass =
  'w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-sm placeholder:text-ink-muted aria-invalid:border-danger disabled:opacity-60'

export function Field({
  label,
  required = false,
  hint,
  error,
  className,
  children,
}: {
  label: string
  /** Marked with an asterisk, which the control's `required` says to assistive technology. */
  required?: boolean
  /** What to know of the field, under it: one line, or several in their order. */
  hint?: string | readonly string[]
  /** The error to show under the control, already translated. */
  error?: string | null
  className?: string
  children: ReactNode
}) {
  const id = useId()
  const hints = (typeof hint === 'string' ? [hint] : (hint ?? [])).filter(Boolean)
  const hintIds = hints.map((_, i) => (i === 0 ? `${id}-hint` : `${id}-hint-${i}`))
  const errorId = `${id}-error`
  const describedBy = [...hintIds, error ? errorId : null].filter(Boolean).join(' ')
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && (
          <span aria-hidden className="text-danger">
            {' *'}
          </span>
        )}
      </label>
      <FieldContext.Provider
        value={{ id, required, invalid: Boolean(error), describedBy: describedBy || undefined }}
      >
        {children}
      </FieldContext.Provider>
      {hints.map((h, i) => (
        <p key={hintIds[i]} id={hintIds[i]} className="text-xs text-ink-muted">
          {h}
        </p>
      ))}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  )
}

/** A field's error, in red under it; nothing while there is none. */
export function FieldError({ id, children }: { id?: string; children?: string | null }) {
  if (!children) return null
  return (
    <p id={id} className="text-sm text-danger">
      {children}
    </p>
  )
}
